import React, { useState } from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import Button from '@dashboard/components/Button';
import { ReceiptRefundIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { subscriptionPaymentsKey } from '@/dashboard/lib/queryKeys';
import { formatPrice } from '@/dashboard/services/formatting';
import { formatPriceForInput, formatPriceInput } from '@/dashboard/services/formatting/price';
import { calculateMaximumRefundAmount } from '@/dashboard/services/payments';
import clsx from 'clsx';

const PaymentsSubscriptionPaymentRefundDialog = ({ payment, subscription }) => {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const refundPayment = useMutation({
    mutationFn: values =>
      fetcher(
        `/sellers/${subscription.seller}/subscriptions/${subscription._id}/payments/${payment._id}/refund`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionPaymentsKey(subscription._id) });

      toast({
        title: 'Payment refunded',
        type: 'success',
      });

      setOpen(false);
    },
    onError: error => {
      toast({
        title: 'An error occurred while refunding the payment',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const maximumRefundAmount = calculateMaximumRefundAmount(payment);

  if (!payment.paid) {
    return null;
  }

  return (
    <>
      <Button
        appearance="secondary"
        size="small"
        onClick={() => setOpen(true)}
        className={clsx('gap-x-2', {
          'pointer-events-none opacity-50 cursor-not-allowed':
            payment.refunded === true || maximumRefundAmount === 0,
        })}
      >
        Refund payment
        <ReceiptRefundIcon className="w-4 h-4" />
      </Button>

      <Dialog
        title="Refund payment"
        cancelButton={null}
        confirmButton={null}
        size="md"
        open={open}
        onCancel={() => setOpen(false)}
        description="Add a note about this refund."
      >
        {() => (
          <Form
            showState={false}
            initialValues={{
              note: '',
              refundReason: '',
              refundAmount: formatPriceForInput({
                amount: maximumRefundAmount,
                currency: payment.currency,
                style: 'decimal',
              }),
              sendRefundEmail: true,
            }}
            onSubmit={async values => {
              try {
                const data = await refundPayment.mutateAsync({
                  ...values,
                  refundAmount: formatPriceInput(values.refundAmount, payment.currency),
                });

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({
                queryKey: ['subscriptionPayments', subscription._id],
              });

              toast({
                title: 'Payment refunded',
                type: 'success',
              });

              setOpen(false);
            }}
            fields={() => [
              {
                name: 'refundAmount',
                label: 'Refund amount',
                type: 'number',
                tags: [
                  `${formatPrice({ amount: maximumRefundAmount, currency: payment.currency })} left to refund`,
                ],
                required: true,
              },
              {
                name: 'refundReason',
                label: 'Reason',
                type: 'select',
                required: true,
                placeholder: 'Select a reason',
                options: [
                  { value: 'duplicate', label: 'Duplicate' },
                  { value: 'fraudulent', label: 'Fraudulent' },
                  { value: 'requested_by_customer', label: 'Requested by customer' },
                ],
              },
              {
                name: 'reason',
                label: 'Note',
                tags: ['not shown to customer'],
                type: 'textarea',
              },
              {
                name: 'sendRefundEmail',
                label: 'Send refund email to customer',
                type: 'checkbox',
              },
            ]}
            formSubmitAlign="end"
            submitButton={{
              label: 'Refund payment',
              appearance: 'primary',
            }}
          />
        )}
      </Dialog>
    </>
  );
};

export default PaymentsSubscriptionPaymentRefundDialog;
